import { Bullet, Label, Series, p50 } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CandlestickSeries,
  CategoryAxis,
  StepLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, orderBy } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

export default class BoxPlot extends BaseChart {
  declare chart: XYChart;
  xRenderer!: AxisRendererX;
  yRenderer!: AxisRendererY;
  yAxis!: CategoryAxis<AxisRenderer> | ValueAxis<AxisRenderer>;
  xAxis!: CategoryAxis<AxisRenderer> | ValueAxis<AxisRenderer>;
  candleStickSeries!: CandlestickSeries[];
  isHorizontal!: boolean;

  initChart() {
    this.root.container.children.clear();
    if (this.chart) {
      this.chart.series.clear();
    }

    this.createChart();
    this.createRenderers();
    this.createAxes();
    this.createAxesTitles();
    this.createCursor();

    this.candleStickSeries = [];
    const paletteLength = this.palette.length;
    const seriesNames: string[] = [];

    this.data.forEach((dataItem: Record<string, unknown>, index: number) => {
      const seriesName = dataItem[this.settings.decompositionMetric] as string;
      seriesNames.push(seriesName);
      this.createSeries(dataItem, seriesName, index % paletteLength);
    });

    this.showLegend(seriesNames, this.candleStickSeries as LegendData, true, false);
  }

  protected mapData(data: ChartDataRecord[]) {
    this.isHorizontal = this.options.isHorizontal as boolean;

    let preparedData: ChartDataRecord[] = cloneDeep(data);

    const boxFields = ["_min", "_25th", "_median", "_75th", "_max"].map(
      (suffix) => `${this.settings.displayMetric}${suffix}`
    );
    preparedData = preparedData.map((item) => {
      const filled = { ...item };
      boxFields.forEach((field) => {
        if (typeof filled[field] !== "number") {
          filled[field] = 0;
        }
      });
      return filled;
    });

    preparedData = preparedData.map((dataItem: ChartDataRecord) => {
      if (dataItem[this.settings.decompositionMetric] === null) {
        return { ...dataItem, [this.settings.decompositionMetric]: Lang.null };
      }

      if (dataItem[this.settings.decompositionMetric] === "other") {
        return { ...dataItem, [this.settings.decompositionMetric]: Lang.other };
      }

      return {
        ...dataItem,
        [this.settings.decompositionMetric]: dataItem[this.settings.decompositionMetric] as string,
      };
    });

    const orderFunc = (el: ChartDataRecord) => {
      const val = el[`${this.settings.displayMetric}_median`];
      return val === null ? -Infinity : val;
    };

    preparedData = orderBy(preparedData, orderFunc, this.isHorizontal ? "asc" : "desc");
    return this.showItems(preparedData, this.settings.decompositionMetric, undefined);
  }

  createSeries(dataItem: Record<string, unknown>, seriesName: string, index: number) {
    const color = this.getSeriesColor(index);
    const categoryAxis = this.isHorizontal ? "Y" : "X";
    const valueAxis = this.isHorizontal ? "X" : "Y";

    const series = this.chart.series.push(
      CandlestickSeries.new(this.root, {
        name: seriesName,
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        [`highValue${valueAxis}Field`]: `${this.settings.displayMetric}_max`,
        [`openValue${valueAxis}Field`]: `${this.settings.displayMetric}_75th`,
        [`value${valueAxis}Field`]: `${this.settings.displayMetric}_25th`,
        [`lowValue${valueAxis}Field`]: `${this.settings.displayMetric}_min`,
        [`category${categoryAxis}Field`]: this.settings.decompositionMetric,
        clustered: false,
        fill: color,
        stroke: color,
      })
    );

    series.columns.template.adapters.add("fill", () => {
      return color;
    });
    series.columns.template.adapters.add("stroke", () => {
      return color;
    });

    series.data.setAll([dataItem]);
    this.candleStickSeries.push(series);

    const medianSeries = this.chart.series.push(
      StepLineSeries.new(this.root, {
        name: seriesName,
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        [`value${valueAxis}Field`]: `${this.settings.displayMetric}_median`,
        [`category${categoryAxis}Field`]: this.settings.decompositionMetric,
        noRisers: true,
        stroke: color,
        fill: color,
      })
    );
    medianSeries.data.setAll([dataItem]);

    if (this.settings.showDataLabels) {
      const metricFormatter = this.formatters?.[this.settings.displayMetric];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      medianSeries.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          fontSize: labelFontSize,
          centerX: p50,
        });

        if (this.isHorizontal) {
          label.setAll({ centerY: p50, dx: 32 });
        } else {
          label.setAll({ dy: -32 });
        }

        label.adapters.add("text", () => {
          const value = this.isHorizontal ? dataItem?.get("valueX") : dataItem?.get("valueY");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const value = this.isHorizontal ? dataItem?.get("valueX") : dataItem?.get("valueY");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, {
          locationX: this.isHorizontal ? 1 : undefined,
          locationY: this.isHorizontal ? undefined : 1,
          sprite: label,
        });
      });
    }

    this.createTooltip(series as Series, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          param.push({ key, value });
        });
      }
      return param;
    });
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        focusable: true,
      })
    );
  }

  protected createRenderers() {
    this.xRenderer = AxisRendererX.new(this.root, {
      minGridDistance: 60,
    });

    this.yRenderer = AxisRendererY.new(this.root, {});

    this.xRenderer.labels.template.setAll({
      ...chartFontSettings.label,
      centerY: p50,
      centerX: p50,
    });

    this.yRenderer.labels.template.setAll({
      ...chartFontSettings.label,
    });
  }

  protected createAxes() {
    const categoryAxis = (this.isHorizontal ? this.chart.yAxes : this.chart.xAxes).push(
      CategoryAxis.new(this.root, {
        renderer: this.isHorizontal ? this.yRenderer : this.xRenderer,
        categoryField: this.settings.decompositionMetric,
        paddingTop: this.isHorizontal ? 0 : 5,
      })
    );

    const valueAxis = (this.isHorizontal ? this.chart.xAxes : this.chart.yAxes).push(
      ValueAxis.new(this.root, {
        renderer: this.isHorizontal ? this.xRenderer : this.yRenderer,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.displayMetric, this.data),
        paddingTop: this.isHorizontal ? 5 : 0,
        extraMax: this.module === PLAN_MODULE && this.settings.showDataLabels ? 0.1 : 0,
      })
    );

    categoryAxis.data.setAll(this.data);

    const renderer = this.isHorizontal ? this.yRenderer : this.xRenderer;

    renderer.set("minGridDistance", 1);

    const getCategoryCellSize = () => {
      const plotSize = this.isHorizontal
        ? this.chart.plotContainer.getPrivate("height") ?? 0
        : this.chart.plotContainer.getPrivate("width") ?? 0;

      const count = categoryAxis?.dataItems?.length ?? 0;
      return count > 0 ? plotSize / count : plotSize;
    };

    const updateCategoryAxisLabelsLayout = () => {
      if (this.isHorizontal) {
        const chartWidth = this.chart.getPrivate("width") ?? 0;
        const maxWidth = Math.max(40, Math.floor(chartWidth * 0.2));
        const maxHeight = Math.max(12, Math.floor(getCategoryCellSize() - 4));

        renderer.labels.template.setAll({
          ...chartFontSettings.label,
          maxWidth,
          maxHeight,
          textAlign: "right",
          centerY: p50,
          oversizedBehavior: "truncate",
          tooltipText: `{${this.settings.decompositionMetric}}`,
          visible: true,
        });
        return;
      }

      const cellSize = getCategoryCellSize();
      const padding = 16;
      const maxWidth = Math.max(40, Math.floor(cellSize - padding));

      const behavior = cellSize < 70 ? "truncate" : "wrap";

      renderer.labels.template.setAll({
        ...chartFontSettings.label,
        maxWidth,
        textAlign: "center",
        centerX: p50,
        centerY: p50,
        oversizedBehavior: behavior,
        tooltipText: `{${this.settings.decompositionMetric}}`,
        visible: true,
      });
    };

    this.chart.plotContainer.onPrivate("width", updateCategoryAxisLabelsLayout);
    this.chart.plotContainer.onPrivate("height", updateCategoryAxisLabelsLayout);
    categoryAxis.events.on("datavalidated", updateCategoryAxisLabelsLayout);

    updateCategoryAxisLabelsLayout();

    this.xAxis = this.isHorizontal ? valueAxis : categoryAxis;
    this.yAxis = this.isHorizontal ? categoryAxis : valueAxis;
  }

  protected createAxesTitles() {
    const xAxisTitle = this.isHorizontal ? this.settings.displayMetricTitle : this.settings.decompositionMetricTitle;
    const yAxisTitle = this.isHorizontal ? this.settings.decompositionMetricTitle : this.settings.displayMetricTitle;

    if (xAxisTitle) {
      this.xAxis.children.push(
        Label.new(this.root, {
          text: xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (yAxisTitle) {
      this.yAxis.children.unshift(
        Label.new(this.root, {
          text: yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: p50,
          centerX: p50,
        })
      );
    }
  }

  protected createCursor() {
    const cursor = this.chart.set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.xAxis,
      })
    );

    cursor.lineX.set("visible", !this.isHorizontal);
    cursor.lineY.set("visible", this.isHorizontal);
  }

  protected chartDataFields(): string[] {
    const getComputedFields = (metric: string) => [
      `${metric}_max`,
      `${metric}_75th`,
      `${metric}_25th`,
      `${metric}_min`,
      `${metric}_median`,
    ];

    const displaySettingsNames = ["displayMetric", "decompositionMetric"];

    const existingSettings = Object.keys(this?.settings || {}).filter((key) => displaySettingsNames.includes(key));

    return [
      ...existingSettings.map((settingName) => this.settings[settingName]),
      ...getComputedFields(this.settings.displayMetric),
    ];
  }
}
